import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type AdminNotice = components['schemas']['AdminNotice'];
export type NoticeCreate = components['schemas']['NoticeCreate'];
export type NoticeUpdate = components['schemas']['NoticeUpdate'];
export type UserNotice = components['schemas']['UserNotice'];

/** Every notice with its status for the API's today, and that date. */
export function useAdminNotices() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.adminNotices(),
    queryFn: () => unwrap(api.GET('/api/admin/notices')),
  });
}

/** Saves, ends or deletes notices; each refreshes the list. */
export function useNoticeMutations() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminNotices() });
  const path = (id: string) => ({ params: { path: { notice_id: id } } });
  return {
    create: useMutation({
      mutationFn: (body: NoticeCreate) => unwrap(api.POST('/api/admin/notices', { body })),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: NoticeUpdate }) =>
        unwrap(api.PATCH('/api/admin/notices/{notice_id}', { ...path(id), body })),
      onSuccess,
    }),
    end: useMutation({
      mutationFn: (id: string) => unwrap(api.POST('/api/admin/notices/{notice_id}/end', path(id))),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => unwrap(api.DELETE('/api/admin/notices/{notice_id}', path(id))),
      onSuccess,
    }),
  };
}

/** The signed-in user's active, unacknowledged notices, newest first. */
export function useMyNotices(enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.myNotices(),
    queryFn: async () => (await unwrap(api.GET('/api/notices'))).items,
    enabled,
  });
}

/** Records the user's acknowledgement; the notice then leaves their list. */
export function useAcknowledgeNotice() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/notices/{notice_id}/acknowledge', { params: { path: { notice_id: id } } }),
      ),
    onSuccess: (_, id) =>
      queryClient.setQueryData<UserNotice[]>(queryKeys.myNotices(), (list) =>
        list?.filter((notice) => notice.id !== id),
      ),
  });
}
